import { Component, EventEmitter, Output, Input } from '@angular/core';
import { WatchAccountService } from '../../../../services/watch-account.service';
import { AccountService } from '../../../../services/account.service';
import { Observable } from 'rxjs';
import {
  ITag,
  IGroup,
  IFunnel,
  ILabel,
} from '../../../../interfaces/graph-types';
import * as moment from 'moment';

@Component({
  selector: 'candidate-advanced-filter',
  template: require('./candidate-advanced-filter.component.pug'),
  styles: [require('./candidate-advanced-filter.component.css').toString()],
})
export class CandidatesAdvancedFilterComponent {
  @Output()
  onClick = new EventEmitter<any>();

  @Input()
  searchFilter;

  isCollapsed: boolean = true;

  tags$: Observable<ITag[]>;
  groups$: Observable<IGroup[]>;
  funnels$: Observable<IFunnel[]>;

  today = new Date(Date.now()).setHours(0, 0, 0);
  placeholder = moment(this.today).format('L LT'); // placeholder matching bsDatePicker's format
  formatHelp = `Date format: "${this.placeholder}" (${
    moment.localeData()['_longDateFormat']['L']
  })`;
  maxDate = new Date();
  bsCreatedAfter;
  bsCreatedBefore;

  bsConfig = {
    dateInputFormat: 'L LT', // locale date and locale time
    containerClass: 'theme-default',
    isAnimated: true,
    returnFocusToInput: true,
    showWeekNumbers: false,
  };

  applicationError = null;
  applicationErrorStatus = [
    {
      name: 'Ignore',
      id: 'applicationErrorIgnore',
      value: null,
    },
    {
      name: 'Has Error',
      id: 'applicationErrorTrue',
      value: true,
    },
    {
      name: 'Does NOT have Error',
      id: 'applicationErrorFalse',
      value: false,
    },
  ];

  doNotContact = null;
  doNotContactStatus = [
    {
      name: 'Ignore',
      id: 'doNotContactIgnore',
      value: null,
    },
    {
      name: 'Has "Do Not Contact"',
      id: 'doNotContactTrue',
      value: true,
    },
    {
      name: 'Does NOT have "Do Not Contact"',
      id: 'doNotContactFalse',
      value: false,
    },
  ];

  emailVerifications = [];
  emailStatus = [
    {
      name: 'Not Verified',
      id: 0,
    },
    {
      name: 'Invalid',
      id: -1,
    },
    {
      name: 'Valid',
      id: 1,
    },
    {
      name: 'Risky',
      id: 2,
    },
    {
      name: 'Unknown Validity',
      id: 3,
    },
  ];

  applicationStages = [];
  applicationStatus = [
    {
      name: 'Waiting',
      id: 10,
    },
    {
      name: 'Starting...',
      id: 20,
    },
    {
      name: 'Running',
      id: 30,
    },
    {
      name: 'Bounced',
      id: 40,
    },
    {
      name: 'Unsubscribed',
      id: 50,
    },
    {
      name: 'Canceled',
      id: 60,
    },
    {
      name: 'Out Of Office',
      id: 70,
    },
    {
      name: 'Replied',
      id: 100,
    },
    {
      name: 'Booked',
      id: 130,
    },
    {
      name: 'Completed',
      id: 500,
    },
  ];

  notInGroup = null;

  includedTags = [];
  excludedTags = [];
  includedFunnels = [];
  excludedFunnels = [];
  includedGroups = [];
  excludedGroups = [];
  includedLabels = [];
  excludedLabels = [];

  // -------------------------------------------------------------------
  constructor(
    private watch: WatchAccountService,
    private accountService: AccountService,
  ) {
    this.maxDate.setDate(this.maxDate.getDate());
  }

  // -------------------------------------------------------------------
  ngOnInit() {
    this.funnels$ = this.accountService.funnelNames(
      this.watch.accountId().getValue(),
      null,
      null,
    );
    this.tags$ = this.accountService.tags(this.watch.accountId().getValue());
    this.groups$ = this.accountService.groups(
      this.watch.accountId().getValue(),
    );
    // this is a workaround for a bsDatePicker bug: https://github.com/valor-software/ngx-bootstrap/issues/3553
    // we set a timeout to populate these values after the datepicker is initialized
    setTimeout(() => {
      this.bsCreatedAfter =
        this.searchFilter && this.searchFilter.created_after
          ? new Date(this.searchFilter.created_after * 1000)
          : null;
      this.bsCreatedBefore =
        this.searchFilter && this.searchFilter.created_before
          ? new Date(this.searchFilter.created_before * 1000)
          : null;
    }, 0);
  }

  // -------------------------------------------------------------------
  applicationErrorChanged(id) {
    const status = this.applicationErrorStatus.find(status => status.id === id);
    const value = status === undefined ? null : status.value;

    this.applicationError = value;
    this.searchFilter.application_error = value;
  }

  // -------------------------------------------------------------------
  doNotContactChanged(id) {
    const status = this.doNotContactStatus.find(status => status.id === id);
    const value = status === undefined ? null : status.value;

    this.doNotContact = value;
    this.searchFilter.do_not_contact = value;
  }

  // -------------------------------------------------------------------
  notInGroupChanged(_value: any, onsuccess: onSmartSuccess) {
    onsuccess();
    this.notInGroup = !this.notInGroup;
    this.searchFilter.not_in_group = this.notInGroup || null;

    if (this.notInGroup) {
      this.searchFilter.exclude_groups = [];
      this.searchFilter.include_groups = [];
      this.includedGroups = [];
      this.excludedGroups = [];
    }
  }

  // -------------------------------------------------------------------
  onCreatedAfterValueChange(value: any): void {
    this.bsCreatedAfter = value;
    if (value) {
      this.searchFilter.created_after = value.valueOf() / 1000;
    } else {
      this.searchFilter.created_after = null;
    }
  }

  // -------------------------------------------------------------------
  onCreatedBeforeValueChange(value: any): void {
    this.bsCreatedBefore = value;
    if (value) {
      this.searchFilter.created_before = value.valueOf() / 1000;
    } else {
      this.searchFilter.created_before = null;
    }
  }

  // -------------------------------------------------------------------
  funnelEnabled(funnel, filter) {
    return filter.includes(funnel.id);
  }

  // -------------------------------------------------------------------
  funnelingInclude(
    funnel: IFunnel,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    const index = this.searchFilter.include_funnels.indexOf(funnel.id);
    if (index > -1) {
      this.searchFilter.include_funnels.splice(index, 1);
      this.includedFunnels.splice(index, 1);
    } else {
      this.searchFilter.include_funnels.push(funnel.id);
      this.includedFunnels.push(funnel.name);
    }
    onsuccess();
  }

  // -------------------------------------------------------------------
  funnelingExclude(
    funnel: IFunnel,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    const index = this.searchFilter.exclude_funnels.indexOf(funnel.id);
    if (index > -1) {
      this.searchFilter.exclude_funnels.splice(index, 1);
      this.excludedFunnels.splice(index, 1);
    } else {
      this.searchFilter.exclude_funnels.push(funnel.id);
      this.excludedFunnels.push(funnel.name);
    }
    onsuccess();
  }

  // -------------------------------------------------------------------
  funnelExplanation() {
    let text = [];
    if (this.includedFunnels.length > 0) {
      text.push(
        `have started the funnel '${this.includedFunnels.join("' or '")}'${
          this.includedFunnels.length > 1 ? ' (at least one of them)' : ''
        }`,
      );
    }

    if (this.excludedFunnels.length > 0) {
      text.push(
        `have not started the funnel${
          this.excludedFunnels.length > 1 ? 's' : ''
        } '${this.excludedFunnels.join("' or '")}'`,
      );
    }

    return text.join(' AND ');
  }

  // -------------------------------------------------------------------
  groupEnabled(group, filter) {
    return filter.includes(group.id);
  }

  // -------------------------------------------------------------------
  groupingInclude(
    group: IGroup,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    const index = this.searchFilter.include_groups.indexOf(group.id);
    if (index > -1) {
      this.searchFilter.include_groups.splice(index, 1);
      this.includedGroups.splice(index, 1);
    } else {
      this.searchFilter.include_groups.push(group.id);
      this.includedGroups.push(group.name);
    }
    onsuccess();
  }

  // -------------------------------------------------------------------
  groupingExclude(
    group: IGroup,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    const index = this.searchFilter.exclude_groups.indexOf(group.id);
    if (index > -1) {
      this.searchFilter.exclude_groups.splice(index, 1);
      this.excludedGroups.splice(index, 1);
    } else {
      this.searchFilter.exclude_groups.push(group.id);
      this.excludedGroups.push(group.name);
    }
    onsuccess();
  }

  // -------------------------------------------------------------------
  groupExplanation() {
    let text = [];
    if (this.includedGroups.length > 0) {
      text.push(`are in group '${this.includedGroups.join("' or in '")}'`);
    }

    if (this.excludedGroups.length > 0) {
      const extra =
        this.includedGroups.length > 0
          ? ''
          : ' (including candidates in no group)';
      text.push(
        `are not in group '${this.excludedGroups.join("' nor in '")}'${extra}`,
      );
    }

    return text.join(' AND ');
  }

  // -------------------------------------------------------------------
  tagEnabled(tag, list) {
    return list.includes(tag.id);
  }

  // -------------------------------------------------------------------
  taggingInclude(
    tag: ITag,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    const index = this.searchFilter.include_tags.indexOf(tag.id, 0);
    if (index > -1) {
      this.searchFilter.include_tags.splice(index, 1);
      this.includedTags.splice(index, 1);
    } else {
      this.searchFilter.include_tags.push(tag.id);
      this.includedTags.push(tag.name);
    }
    onsuccess();
  }

  // -------------------------------------------------------------------
  taggingExclude(
    tag: ITag,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    const index = this.searchFilter.exclude_tags.indexOf(tag.id, 0);
    if (index > -1) {
      this.searchFilter.exclude_tags.splice(index, 1);
      this.excludedTags.splice(index, 1);
    } else {
      this.searchFilter.exclude_tags.push(tag.id);
      this.excludedTags.push(tag.name);
    }
    onsuccess();
  }

  // -------------------------------------------------------------------
  tagExplanation() {
    let text = [];
    if (this.includedTags.length > 0) {
      text.push(
        `have the tag${
          this.includedTags.length > 1 ? 's' : ''
        } '${this.includedTags.join("' and '")}'`,
      );
    }

    if (this.excludedTags.length > 0) {
      text.push(
        `do not have the tag${
          this.excludedTags.length > 1 ? 's' : ''
        } '${this.excludedTags.join("' or '")}'`,
      );
    }

    return text.join(' AND ');
  }

  // -------------------------------------------------------------------
  changeDetected(
    data: any,
    newValue: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    if (data == 'min_opens') {
      this.searchFilter.min_opens = newValue;
    }
    if (data == 'max_opens') {
      this.searchFilter.max_opens = newValue;
    }

    if (data == 'min_clicks') {
      this.searchFilter.min_clicks = newValue;
    }
    if (data == 'max_clicks') {
      this.searchFilter.max_clicks = newValue;
    }

    if (data == 'min_replies') {
      this.searchFilter.min_replies = newValue;
    }
    if (data == 'max_replies') {
      this.searchFilter.max_replies = newValue;
    }

    if (data == 'min_funnels') {
      this.searchFilter.min_funnels = newValue;
    }
    if (data == 'max_funnels') {
      this.searchFilter.max_funnels = newValue;
    }

    if (data == 'min_step') {
      this.searchFilter.min_step = newValue;
    }
    if (data == 'max_step') {
      this.searchFilter.max_step = newValue;
    }

    onsuccess();
  }

  // -------------------------------------------------------------------
  statusEnabled(status) {
    return this.applicationStages.includes(status.id);
  }

  // -------------------------------------------------------------------
  statuing(status: any, onsuccess: onSmartSuccess, onfailure: onSmartFailure) {
    const index = this.searchFilter.application_status.indexOf(status.id, 0);
    if (index > -1) {
      this.searchFilter.application_status.splice(index, 1);
      this.applicationStages.splice(index, 1);
    } else {
      this.searchFilter.application_status.push(status.id);
      this.applicationStages.push(status.id);
    }
    onsuccess();
  }

  // -------------------------------------------------------------------
  emailStatusEnabled(status) {
    return this.emailVerifications.includes(status.id);
  }

  // -------------------------------------------------------------------
  emailStatuing(
    status: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    const index = this.searchFilter.email_status.indexOf(status.id, 0);
    if (index > -1) {
      this.searchFilter.email_status.splice(index, 1);
      this.emailVerifications.splice(index, 1);
    } else {
      this.searchFilter.email_status.push(status.id);
      this.emailVerifications.push(status.id);
    }
    onsuccess();
  }

  // -------------------------------------------------------------------
  toggle(data: boolean, onsuccess: onSmartSuccess, onfailure: onSmartFailure) {
    data = !data;
    onsuccess();
  }

  // -------------------------------------------------------------------
  cancel() {
    this.doNotContact = null;
    this.applicationError = null;
    this.bsCreatedAfter = null;
    this.bsCreatedBefore = null;
    this.notInGroup = null;
    this.emailVerifications = [];
    this.applicationStages = [];

    this.includedTags = [];
    this.excludedTags = [];
    this.includedFunnels = [];
    this.excludedFunnels = [];
    this.includedGroups = [];
    this.excludedGroups = [];
    this.includedLabels = [];
    this.excludedLabels = [];

    this.onClick.emit();
  }

  // -------------------------------------------------------------------
  apply() {
    this.onClick.emit(this.searchFilter);
  }

  // // -------------------------------------------------------------------
  // noFunnel(data: any, onsuccess: onSmartSuccess, onfailure: onSmartFailure) {
  //   this.searchFilter.no_funnel = !this.searchFilter.no_funnel;
  //   onsuccess();
  // }
}
